import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { BuyerResearchRefinementDto } from "@/api/buyerResearch";
import { Button, StatusBadge, Timestamp } from "@/design-system/components/ui";
import { useUserTimeZone } from "@/utils/datetime";
import { researchCopy } from "./researchCopy";
import { formatCost, refinementStatusTone } from "./researchFormat";

/**
 * Every refinement this run has ever had — what was asked, what Phoenix made of
 * it, what it did and what it spent.
 *
 * <p>🔴 <b>RF13 — A SIBLING OF THE RUN'S TRANSCRIPT, NEVER AN EXTENSION OF
 * IT.</b> {@link ResearchQueryTranscript} is the record of what the SEARCH
 * ENGINE did to the query before anything was bought; this is the record of
 * what PEOPLE asked of the results afterwards. Folding refinement rows into
 * that transcript would put a specialist's question and a probe's arithmetic in
 * one list under one heading, and a reader would have no way to tell which of
 * them narrowed the search they are looking at.</p>
 *
 * <p>It renders in its own section, next to the panel it is the history of,
 * because that is where a reader asking "what has been tried here?" is
 * standing. Sibling is a structural claim, not an adjacency one.</p>
 *
 * <p>🔴 <b>The server's order is the render order.</b> The history arrives
 * newest first and is rendered in the order it arrives — the same rule
 * `ResearchResults` states for candidates, and for the same reason: a second
 * sort here, even one that agreed today, is a second order free to disagree the
 * next time either moves.</p>
 *
 * <p>Collapsed behind one line by default. A run with a refinement applied has
 * that one stated in full on the panel above; the history is the answer to a
 * question most readers are not asking.</p>
 */
export function AskPhoenixHistory({
  refinements,
}: {
  /** Newest first, as the server sends it. */
  refinements: BuyerResearchRefinementDto[];
}) {
  const copy = researchCopy.askPhoenix;
  const [open, setOpen] = useState(false);

  if (refinements.length === 0) return null;

  return (
    <section className="grid gap-space-1" data-testid="ask-phoenix-history">
      <div>
        <Button
          type="button"
          variant="text"
          size="sm"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          data-testid="ask-phoenix-history-toggle"
        >
          {open ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
          {open ? copy.historyHide : copy.historyShow} ({refinements.length})
        </Button>
      </div>
      {open && (
        <ul className="m-0 p-0 list-none grid gap-space-2" data-testid="ask-phoenix-history-list">
          {refinements.map((r) => (
            <li key={r.id}>
              <HistoryRow refinement={r} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One refinement, read-only.
 *
 * <p>🔴 <b>The specialist's own words, verbatim.</b> Decision 5 makes a
 * refinement sticky on the run, so the history is read by people who did not
 * type any of it — a paraphrase, or Phoenix's rationale standing in for the
 * instruction, would make the record an account of what Phoenix thought rather
 * than of what was asked.</p>
 */
function HistoryRow({ refinement: r }: { refinement: BuyerResearchRefinementDto }) {
  const copy = researchCopy.askPhoenix;
  const tz = useUserTimeZone();

  return (
    <article
      className="rounded-md border border-border-light bg-white-pure px-space-3 py-space-2 grid gap-[2px]"
      data-testid="ask-phoenix-history-row"
      data-kind={r.kind}
      data-status={r.status}
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <span className="text-small text-onyx break-words">“{r.text}”</span>
        <StatusBadge tone={refinementStatusTone(r.status)}>
          {copy.status[r.status] ?? r.status}
        </StatusBadge>
      </div>

      <span className="text-caption text-gray-mid break-words">
        <Timestamp value={r.createdAt} tz={tz} /> · {copy.askedBy(r.requestedByName)}
      </span>

      {r.rationale && (
        <span className="text-caption text-gray-mid break-words" data-testid="ask-phoenix-history-rationale">
          {r.rationale}
        </span>
      )}

      {/* 🔴 WHAT WAS IN FORCE WHEN THIS ROW RAN. The counts below describe the
          WHOLE chain, so a composed row showing only its own words would
          attribute a two-step outcome to one instruction. Read back from what
          this refinement stored, never re-derived from today's chain — a
          history row must describe the composition it actually had. */}
      {r.summary && (
        <span className="text-caption text-gray-mid break-words" data-testid="ask-phoenix-history-chain">
          {copy.chain} {r.summary}
        </span>
      )}

      {/* A refusal or a failure says why. An outcome-count line on either would
          report a lens that was never put on. */}
      {r.refusalReason ? (
        <span className="text-caption text-gray-mid break-words" data-testid="ask-phoenix-history-refusal">
          {r.refusalReason}
        </span>
      ) : r.error ? (
        <span className="text-caption text-status-error break-words" data-testid="ask-phoenix-history-error">
          {r.error}
        </span>
      ) : (
        <span className="text-caption text-gray-mid tabular-nums" data-testid="ask-phoenix-history-counts">
          {copy.outcomeCounts(r.visibleCount, r.hiddenCount, r.promotedCount)}
        </span>
      )}

      {/* 🔴 What it SPENT, on the two budgets it spent on, and each named. A
          refinement that bought nothing says nothing here rather than
          reassuring anybody with a zero. */}
      {(r.recordsUsed > 0 || r.estimatedCostUsd > 0) && (
        <span className="text-caption text-gray-mid break-words" data-testid="ask-phoenix-history-spent">
          {[
            r.recordsUsed > 0 ? copy.spent(r.recordsUsed) : null,
            r.estimatedCostUsd > 0 ? copy.costScoringOnly(formatCost(r.estimatedCostUsd)) : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </span>
      )}
    </article>
  );
}
